import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import { useGoogleLogin } from "@react-oauth/google";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { Fragment, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { connect, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import zxcvbn from "zxcvbn";
import Color from "../../../assets/colors";
import SocialLoginButton from "../../../components/onboarding/SocialLoginButton/SocialLoginButton";
import * as config from "../../../config/config";
import { setItem } from "../../../helpers/data_management";
import { posthogSignup } from "../../../helpers/posthog_management";
import { triggerSnackBar } from "../../../helpers/snackbar_management";
import {
  sendTrackingEvent,
  signupTracking,
} from "../../../helpers/tracking_management";
import useTriggerSnackbar from "../../../hooks/useTriggerSnackbar";
import {
  updateJobAlertVisitorEmailDialog,
  updateResumeConfirmationDialog,
  updateSignUpProfileDialog,
} from "../../../redux/actions/dialog_action";
import {
  hideSnackBar,
  showSnackBar,
} from "../../../redux/actions/snackbar_action";
import { register, socialMediaLogin, updatePDPAStatus } from "../../../redux/actions/user_action";
import { store } from "../../../redux/stores/store";
import * as types from "../../../redux/types/user_type";
import {
  ActionLabelStyled,
  BoxContainer,
  ButtonContainer,
  CheckStyled,
  CheckboxStyled,
  CheckedIcon,
  CircularProgressStyled,
  CustomDivider,
  CustomDividerContainer,
  DialogContentStyled,
  DisclaimerContainer,
  EmailSignUpFormContainer,
  EmailSignUpFormContent,
  FormTitle,
  GoogleButton,
  InfoIconStyled,
  LabelDivider,
  LabelStyled,
  LinkedInButton,
  PasswordContainer,
  PasswordInfoText,
  PasswordStrength,
  PasswordStrengthBar,
  PasswordStrengthInfo,
  PasswordStrengthText,
  ReferralLabel,
  SSOSignUpContainer,
  SignUpButton,
  SocialSpinnerContainer,
  StyledLink,
  TextInput,
  TextStyled,
  UncheckedIcon,
  VisibilityIconStyled,
  VisibilityOffIconStyled,
} from "./styles";

function TextInputFunction(props) {
  const {
    label,
    handleChange,
    type,
    email,
    password,
    onKeyDown,
    passwordShown,
    togglePassword,
    registering,
  } = props;

  return (
    <Fragment>
      <TextInput
        disabled={registering}
        data-testid={type == "email" ? "signup-email-input" : "signup-password-input"}
        label={label}
        value={type == "email" ? email : password}
        type={`${type}`}
        onChange={handleChange}
        id={`filled-required-${label}`}
        variant="filled"
        password_shown={passwordShown ? "true" : "false"}
        InputProps={{
          endAdornment: (
            <InputAdornment
              position="start"
              style={{ display: `${type == "email" ? "none" : "flex"}` }}
            >
              {passwordShown ? (
                <VisibilityIconStyled onClick={togglePassword} />
              ) : (
                <VisibilityOffIconStyled onClick={togglePassword} />
              )}
            </InputAdornment>
          ),
        }}
        onKeyDown={onKeyDown}
      />
    </Fragment>
  );
}

let isRegistering = false;
const FORCE_SIGNUP_COMPLETED_KEY = "forceSignupCompleted";

function getAppId(ssoLogin) {
  switch (ssoLogin) {
    case "Continue with Facebook": {
      return process.env.FACEBOOK_APP_ID;
    }
    case "Continue with Google": {
      return process.env.GOOGLE_CLIENT_ID;
    }
    case "Continue with Linkedin": {
      return process.env.LINKEDIN_ID;
    }
  }
}

function SignUp(props) {
  const router = useRouter();
  const { t } = useTranslation();
  const userEmail =
    store.getState().user.user.email || store.getState().jobs.jobAlertEmail;
  const [values, setValues] = useState({
    email: router.query.hiredlyx ? router.query.email : "",
    password: "",
    pdpaAccepted: false,
    error: "",
    loading: false,
    message: "",
  });
  const { email, password, pdpaAccepted } = values;
  const [triggerSnackbarFunc] = useTriggerSnackbar();

  // for posthog tracking
  const fypTab = Number(useSelector((state) => state.jobs.fypTab));
  const signUpOrigin = useSelector((state) => state?.navbar?.signUpOrigin);
  const forceSignupGateActive = useSelector(
    (state) => state?.navbar?.forceSignupGateActive
  );
  const selectedCompany = useSelector(
    (state) => state?.companies?.selectedCompany
  );
  const selectedJob = useSelector((state) => state?.jobs?.job);
  const currentCompany =
    Object.keys(selectedJob).length > 0 ? selectedJob.company : selectedCompany;

  const currentUrl = router.asPath;

  // For Google login
  const [hideGoogleButton, setHideGoogleButton] = useState(false);

  const onFailure = (response) => {
    if (
      response.details?.includes(
        "Cookies are not enabled in current environment."
      )
    ) {
      setHideGoogleButton(true);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      const params = {
        _provider: "google",
        _token: {
          accessToken: tokenResponse.access_token,
        },
      };
      handleSocialLogin(params);
    },
    onError: (errorResponse) => onFailure(errorResponse),
  });

  const { signupMedium, signupSource } = signupTracking(
    currentUrl,
    signUpOrigin,
    selectedJob,
    selectedCompany,
    false,
    null
  );

  // Pre-fill sign up email if log in email doesn't exist
  useEffect(() => {
    if (userEmail) {
      setValues({ email: userEmail });
    }
  }, [userEmail]);

  const [pdpaChecked, setPdpaChecked] = useState(false);

  const [passwordShown, setPasswordShown] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");

  const getPasswordStrengthLabel = (strength) => {
    if (!strength) return "";
    return t(`sign_up_form.password_strength_${strength}`, strength);
  };

  const [showFbNoEmail, setShowFbNoEmail] = useState(false);

  // Password toggle handler
  const togglePassword = () => {
    setPasswordShown(!passwordShown);
  };

  // Function to trigger job seekers who log in via email
  const triggerCustomEventEmailLogin = () => {
    sendTrackingEvent({ event: "email-login" });
  };

  // Function to trigger job seekers who sign up for the first time via email
  const triggerCustomEventFirstTimeEmailSignup = () => {
    if (signUpOrigin === "jobAlert") {
      sendTrackingEvent({
        event: "CE_sign_up_job_alert",
        method: "email",
        origin: "job_alert",
      });
    } else if (signUpOrigin === "job_recommendation" && fypTab === 1) {
      sendTrackingEvent({
        event: "CE_sign_up_rec",
        method: "email",
        origin: "job_recommendation",
      });
    } else {
      sendTrackingEvent({
        event: "first-time-signup",
      });
    }

    posthogSignup(selectedJob, currentCompany, signUpOrigin, "email");
  };

  // Function to trigger job seekers who log in via SSO
  const triggerCustomEventSsoLogin = (provider) => {
    sendTrackingEvent({
      event: `${provider == "google" ? "google-login" : "facebook-login"}`,
    });
  };

  // Function to trigger job seekers who sign up for the first time via SSO
  const triggerCustomEventSsoFirstTimeSignup = (provider) => {
    if (signUpOrigin === "jobAlert") {
      sendTrackingEvent({
        event: "CE_sign_up_job_alert",
        method: provider === "google" ? "google" : "facebook",
        origin: "job_alert",
      });
    } else if (signUpOrigin === "job_recommendation" && fypTab === 1) {
      sendTrackingEvent({
        event: "CE_sign_up_rec",
        method: provider === "google" ? "google" : "facebook",
        origin: "job_recommendation",
      });
    } else {
      sendTrackingEvent({
        event: `${provider == "google"
          ? "google-sso-first-time-signup"
          : "facebook-sso-first-time-signup"
          }`,
      });
    }
    posthogSignup(
      selectedJob,
      currentCompany,
      signUpOrigin,
      provider === "google" ? "google" : "facebook"
    );
  };

  const triggerAccountCreatedSnackbar = () => {
    const cParams = {
      message: "Your account has been successfully created",
      severity: "success",
      accountCreated: true,
    };
    triggerSnackBar(props.hideSnackBar, props.showSnackBar, cParams);
  };

  function handleCheck() {
    setPdpaChecked(!pdpaChecked);
    setValues({ ...values, pdpaAccepted: !values.pdpaAccepted });
  }

  const handleSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }

    if (email == "" || password == "") {
      const cParams = {
        message: "Please key in both email and password to sign up",
        severity: "error",
      };
      triggerSnackBar(props.hideSnackBar, props.showSnackBar, cParams);

      return;
    }

    if (!password || password?.length < 8) {
      const cParams = {
        message: "Please ensure password is minimum 8 characters long",
        severity: "error",
      };
      triggerSnackBar(props.hideSnackBar, props.showSnackBar, cParams);
      return;
    }

    const hasUppercase = /[A-Z]/.test(password);
    const hasSymbol = /[^A-Za-z0-9]/.test(password);

    if (!hasUppercase || !hasSymbol) {
      const cParams = {
        message:
          "Password must include at least 1 symbol and 1 uppercase letter",
        severity: "error",
      };
      triggerSnackBar(props.hideSnackBar, props.showSnackBar, cParams);
      return;
    }

    if (!validatePdpaConsent()) return;

    if (isRegistering) return;

    isRegistering = true;

    let urlParams = document.cookie
      .split("; ")
      .find((row) => row.startsWith("params="))
      ?.toString()
      .replace("params=", "");
    let cEmail = email;
    let symbolIndex = cEmail.indexOf("@");
    let cName = cEmail.substring(0, symbolIndex);
    let mrpId = document.cookie
      .split("; ")
      .find((row) => row.startsWith("mrpId="))
      ?.toString()
      .replace("mrpId=", "");

    const { signupMedium, signupSource } = signupTracking(
      currentUrl,
      signUpOrigin,
      selectedJob,
      selectedCompany,
      false,
      null
    );

    let params = {
      name: cName,
      email: email,
      password: password,
      pdpaAccepted: pdpaAccepted,
      urlParams: urlParams, // To track users who sign up from an advertisement
      referralToken: null, // To track users who sign up via Workanda campaign
      mrpParentId: mrpId, // To track referrer id for talent referral program
      signupMedium: signupMedium,
      signupSource: signupSource,
    };
    console.log("REGISTER PARAMS", params);

    props
      .register(params)
      .then((response) => {
        isRegistering = false;
        if (response.type === types.REGISTER_ACCOUNT_SUCCEED) {
          let userId = response.user.id;
          let userName = response.user.name;
          let userEmail = response.user.email;

          // check if visitor job alert exists and store to local storage
          if (store.getState().jobs.visitorJobAlert) {
            setItem(
              "visitorJobAlert",
              store.getState().jobs.visitorJobAlert.toString()
            );
          }

          if (forceSignupGateActive) {
            sessionStorage.setItem(FORCE_SIGNUP_COMPLETED_KEY, "true");
          }

          // check if visitor sign-up from job alert email mailer
          const visitorUrl = window.location.search;
          if (visitorUrl.includes("?visitor-sign-up")) {
            window.sessionStorage.setItem("visitorJobAlertSignUp", "true");
          }

          if (store.getState().user.isDropCV) {
            // Google enhanced conversion tracking for sign ups on drop resume page
            sendTrackingEvent({
              event: "event_enhanced_conversion",
              enhanced_conversion_data: {
                email: `${userEmail}`,
                phone_number: ``,
                last_name: ``,
                first_name: `${userName}`,
              },
            });

            Cookies.set("userId", userId, { expires: 365 }); // Store user ID to cookies to be used by GA with 1 year expiry
            Cookies.set("isRegistered", userId ? true : false, {
              expires: 365,
            }); // Check if user is registered/logged in and store cookie for CleverTap

            props.handleClose();
            props.updateSignUpProfileDialog(true);
            sessionStorage.setItem("triggerSignUpProfileDialog", "true");
            triggerCustomEventFirstTimeEmailSignup();
            triggerCustomEventEmailLogin();
            triggerAccountCreatedSnackbar();
            // router.reload();
          } else {
            try {
              // Google enhanced conversion tracking for sign ups
              sendTrackingEvent({
                event: "event_enhanced_conversion",
                enhanced_conversion_data: {
                  email: `${userEmail}`,
                  phone_number: ``,
                  last_name: ``,
                  first_name: `${userName}`,
                },
              });

              Cookies.set("userId", userId, { expires: 365 }); // Store user ID to cookies to be used by GA with 1 year expiry
              Cookies.set("isRegistered", userId ? true : false, {
                expires: 365,
              }); // Check if user is registered/logged in and store cookie for CleverTap

              triggerCustomEventFirstTimeEmailSignup();
              triggerCustomEventEmailLogin();
            } catch (error) {
              //
            }

            triggerAccountCreatedSnackbar();
            props.handleClose();
            props.updateSignUpProfileDialog(true);
            sessionStorage.setItem("triggerSignUpProfileDialog", "true");

            // router.reload();
          }
        } else if (response.message == "account exist") {
          // Only allow valid email addresses to sign up
          const validateEmailFormat = new RegExp(
            /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i
          );

          let cParams = {
            message: `${validateEmailFormat.test(email)
              ? "Account already exist"
              : "Email is not valid"
              }`,
            severity: "error",
          };

          triggerSnackBar(props.hideSnackBar, props.showSnackBar, cParams);
        } else if (response.invalidCharacterError) {
          triggerSnackBar(props.hideSnackBar, props.showSnackBar, {
            message: response.message,
            severity: "error",
          });
        }
        props.updateJobAlertVisitorEmailDialog(false);
      })
      .catch((error) => {
        isRegistering = false;
      });
  };

  const handleChange = (name) => (e) => {
    // Password validation strength check
    const password = zxcvbn(e.target.value);

    if (name === "password") {
      if (!e.target.value || e.target.value === "") {
        setPasswordStrength("");
      } else {
        if (password.score > 2) {
          setPasswordStrength("strong");
        } else if (password.score === 2) {
          setPasswordStrength("medium");
        } else {
          setPasswordStrength("poor");
        }
      }
    }

    setValues({ ...values, error: false, [name]: e.target.value });
  };

  const handleSocialLogin = (response) => {
    let provider = response._provider;

    setShowFbNoEmail(false);

    // To track users who sign up from an advertisement
    let urlParams = document.cookie
      .split("; ")
      .find((row) => row.startsWith("params="))
      ?.toString()
      .replace("params=", "");

    const { signupMedium, signupSource } = signupTracking(
      currentUrl,
      signUpOrigin,
      selectedJob,
      selectedCompany,
      true, // isSocialLogin = true
      provider // socialProvider = "google", "linkedin", etc.
    );

    let params = {
      provider: provider,
      accessToken: response._token.accessToken,
      urlParams: urlParams,
      signupMedium: signupMedium,
      signupSource: signupSource,
    };

    props.socialMediaLogin(params).then(async (response) => {
      if (
        response.type === types.SOCIAL_LOGIN_FAILED &&
        response.payload?.noEmail === true
      ) {
        // show the fb no email popup
        return setShowFbNoEmail(true);
      } else if (response.type === types.SOCIAL_LOGIN_SUCCEED) {
        if (values.pdpaAccepted) {
          try {
            const consentResult = await props.updatePDPAStatus();

            if (
              consentResult?.type !== types.FETCH_PDPA_STATUS_SUCCESS ||
              consentResult?.payload !== true
            ) {
              triggerSnackBar(props.hideSnackBar, props.showSnackBar, {
                message: "Unable to save your consent. Please try again.",
                severity: "error",
              });
              return;
            }
          } catch (error) {
            triggerSnackBar(props.hideSnackBar, props.showSnackBar, {
              message: "Unable to save your consent. Please try again.",
              severity: "error",
            });
            return;
          }
        }
        let userId = response.payload.user.id;
        let userName = response.payload.user.name;
        let userEmail = response.payload.user.email;



        if (forceSignupGateActive && response.payload?.newUser) {
          sessionStorage.setItem(FORCE_SIGNUP_COMPLETED_KEY, "true");
        }

        // Check if visitor job alert exists and store to local storage
        if (store.getState().jobs.visitorJobAlert) {
          setItem(
            "visitorJobAlert",
            store.getState().jobs.visitorJobAlert.toString()
          );
        }

        Cookies.set("userId", userId, { expires: 365 }); // Store user ID to cookies to be used by GA with 1 year expiry
        Cookies.set("isRegistered", userId ? true : false, {
          expires: 365,
        }); // Check if user is registered/logged in and store cookie for CleverTap

        localStorage.setItem("job_preference_clicked", 0);

        if (store.getState().user.isDropCV) {
          if (response.payload && response.payload.newUser) {
            // Google enhanced conversion tracking for SSO sign ups on drop resume page
            sendTrackingEvent({
              event: "event_enhanced_conversion",
              enhanced_conversion_data: {
                email: `${userEmail}`,
                phone_number: ``,
                last_name: ``,
                first_name: `${userName}`,
              },
            });

            triggerCustomEventSsoFirstTimeSignup(provider);

            props.updateSignUpProfileDialog(true);
            sessionStorage.setItem("triggerSignUpProfileDialog", "true");

            triggerAccountCreatedSnackbar();
          }
          // router.reload();
        } else if (response.payload && response.payload.newUser) {
          // Google enhanced conversion tracking for SSO sign ups
          sendTrackingEvent({
            event: "event_enhanced_conversion",
            enhanced_conversion_data: {
              email: `${userEmail}`,
              phone_number: ``,
              last_name: ``,
              first_name: `${userName}`,
            },
          });

          triggerCustomEventSsoFirstTimeSignup(provider);
          props.handleClose();
        } else {
          triggerCustomEventSsoLogin(provider);
          if (
            response.payload.user.resume === "" &&
            !response.payload.user.onboardingComplete
          ) {
            props.updateResumeConfirmationDialog(true);
            triggerAccountCreatedSnackbar();
            return props.handleClose();
          }
          // router.reload();
        }
      }
      props.updateJobAlertVisitorEmailDialog(false);
    });
  };

  const handleSocialLoginFailure = (error) => {
    console.error("social login error:", error);
  };

  const handleLinkedInButtonClick = () => {
    const context = {
      currentUrl: router.asPath,
      signUpOrigin: signUpOrigin,
      selectedJob: selectedJob,
      selectedCompany: selectedCompany,
      pdpaAccepted: values.pdpaAccepted,
      timestamp: Date.now(),
    };
    localStorage.setItem("linkedin_context", JSON.stringify(context));

    let oauthUrl = new URL("https://www.linkedin.com/oauth/v2/authorization");
    oauthUrl.searchParams.append("response_type", "code");
    oauthUrl.searchParams.append("client_id", process.env.LINKEDIN_ID);
    oauthUrl.searchParams.append(
      "redirect_uri",
      process.env.LINKEDIN_REDIRECT_URI
    );
    oauthUrl.searchParams.append("scope", process.env.LINKEDIN_SCOPE);

    window.location = oauthUrl;

    // check if visitor job alert exists and store to local storage
    if (store.getState().jobs.visitorJobAlert) {
      setItem(
        "visitorJobAlert",
        store.getState().jobs.visitorJobAlert.toString()
      );
    }
    props.updateJobAlertVisitorEmailDialog(false);
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSubmit(null);
    }
  };

  const getProvider = (ssoLogin) => {
    switch (ssoLogin) {
      case "Continue with Facebook": {
        return "facebook";
      }
      case "Continue with Google": {
        return "google";
      }
      case "Continue with Linkedin": {
        return "facebook";
      }
    }
  };

  const validatePdpaConsent = () => {
    if (!values.pdpaAccepted) {
      setPdpaChecked(true);
      return false;
    }

    return true;
  };

  return (
    <Fragment>
      <DialogContentStyled>
        <FormTitle>{t("sign_up_form.signup")}</FormTitle>
        <EmailSignUpFormContainer>
          <form autoComplete="off">
            <EmailSignUpFormContent>
              <TextInputFunction
                registering={props.registering}
                label={t("sign_up_form.email")}
                type="email"
                email={email}
                handleChange={handleChange("email")}
                onKeyDown={onKeyDown}
              />
              <PasswordContainer item>
                <TextInputFunction
                  registering={props.registering}
                  label={t("sign_up_form.password")}
                  type={passwordShown ? "text" : "password"}
                  password={password}
                  handleChange={handleChange("password")}
                  onKeyDown={onKeyDown}
                  passwordShown={passwordShown}
                  togglePassword={togglePassword}
                />
                <PasswordStrengthBar>
                  <PasswordStrength
                    password_strength={passwordStrength}
                  ></PasswordStrength>
                </PasswordStrengthBar>
                {passwordStrength ? (
                  <PasswordStrengthInfo>
                    <PasswordInfoText>
                      {t("sign_up_form.your_password_is")}{" "}
                      <PasswordStrengthText
                        password_strength={passwordStrength}
                      >
                        {getPasswordStrengthLabel(passwordStrength)}
                      </PasswordStrengthText>
                    </PasswordInfoText>
                    <Tooltip
                      componentsProps={{
                        popper: {
                          sx: {
                            zIndex: 1600,
                          },
                        },
                      }}
                      placement="top"
                      arrow
                      title={t("sign_up_form.password_hint")}
                    >
                      <InfoIconStyled />
                    </Tooltip>
                  </PasswordStrengthInfo>
                ) : (
                  <PasswordStrengthInfo>
                    <PasswordInfoText>{t("sign_up_form.password_suggestion")}</PasswordInfoText>
                    <Tooltip
                      componentsProps={{
                        popper: {
                          sx: {
                            zIndex: 1600,
                          },
                        },
                      }}
                      placement="top"
                      arrow
                      title={t("sign_up_form.password_hint")}
                    >
                      <InfoIconStyled />
                    </Tooltip>
                  </PasswordStrengthInfo>
                )}
              </PasswordContainer>
              <BoxContainer flexDirection={"column"} gap={"0.25rem"}>
                <BoxContainer alignItems={"flex-start"} gap={"0.5rem"}>
                  <CheckboxStyled
                    icon={<UncheckedIcon onError={pdpaChecked} />}
                    onChange={handleCheck}
                    value={pdpaAccepted}
                    checked={pdpaAccepted}
                    checkedIcon={
                      <CheckedIcon>
                        <CheckStyled />
                      </CheckedIcon>
                    }
                  />
                  <LabelStyled checkbox="true">
                    <Trans
                      i18nKey="sign_up_form.accept_privacy_terms"
                      components={[
                        <StyledLink href="/privacy-policy" checkbox="true" />,
                        <StyledLink
                          href="/terms-and-conditions"
                          checkbox="true"
                        />,
                      ]}
                    />
                  </LabelStyled>
                </BoxContainer>
                <BoxContainer>
                  {pdpaChecked && !pdpaAccepted ? (
                    <TextStyled
                      color={Color.error}
                      fontSize={"0.6875rem"}
                      fontWeight={500}
                      lineHeight={"20px"}
                      letterSpacing={"0.15px"}
                    >
                      {t("sign_up_form.agree_to_terms")}
                    </TextStyled>
                  ) : null}
                </BoxContainer>
              </BoxContainer>
            </EmailSignUpFormContent>
          </form>
        </EmailSignUpFormContainer>
        <SignUpButton
          onClick={handleSubmit}
          data-testid="signup-submit-btn"
          variant="contained"
          disabled={props.registering}
        >
          {t("sign_up_form.create_account")}
        </SignUpButton>
        <ButtonContainer>
          <LabelStyled caption="true">{t("sign_up_form.already_have_an_account")}</LabelStyled>
          <ActionLabelStyled
            caption="true"
            className={"sso-button"}
            onClick={() => props.handleClickSignIn()}
            color="primary"
            $log_in_button
          >
            {t("sign_up_form.login")}
          </ActionLabelStyled>
        </ButtonContainer>
        {router.asPath.includes("referral_new_sign_up") ? (
          <ReferralLabel subtitle="true">
            {t("sign_up_form.referral")}
          </ReferralLabel>
        ) : null}
        <Fragment>
          <CustomDividerContainer>
            <CustomDivider />
            <LabelDivider>{t("sign_up_form.or_continue_with")}</LabelDivider>
            <CustomDivider />
          </CustomDividerContainer>
          <SSOSignUpContainer item>
            {props.socialLoggingIn || props.registering ? (
              <SocialSpinnerContainer>
                <CircularProgressStyled />
              </SocialSpinnerContainer>
            ) : (
              <Fragment>
                <GoogleButton
                  onClick={() => {
                    if (!validatePdpaConsent()) return;
                    googleLogin();
                  }}
                  $hide_button={hideGoogleButton ? "true" : "false"}
                  data-testid="signup-google-btn"
                >
                  <img
                    src={
                      config.assetDomain +
                      "/images/hiredly/google-logo-login.svg"
                    }
                    alt="google"
                  />
                </GoogleButton>
                <LinkedInButton onClick={() => {
                  if (!validatePdpaConsent()) return;
                  handleLinkedInButtonClick();
                }}
                  data-testid="signup-linkedin-btn">
                  <img
                    src={
                      config.assetDomain +
                      "/images/hiredly/linkedin-logo-login.svg"
                    }
                    alt="linkedin"
                  />
                </LinkedInButton>
                <SocialLoginButton
                  provider={getProvider("Continue with Facebook")}
                  appId={getAppId("Continue with Facebook")}
                  beforeLogin={validatePdpaConsent}
                  onLoginSuccess={handleSocialLogin}
                  onLoginFailure={handleSocialLoginFailure}
                  dataTestId="signup-facebook-btn"
                >
                  <img
                    src={
                      config.assetDomain +
                      "/images/hiredly/facebook-logo-login.svg"
                    }
                    alt="facebook"
                  />
                </SocialLoginButton>
              </Fragment>
            )}
          </SSOSignUpContainer>
        </Fragment>

        <DisclaimerContainer>
          <LabelStyled disclaimer="true">
            <Trans
              i18nKey="sign_up_form.signup_disclaimer"
              components={[
                <StyledLink href="/privacy-policy" />,
                <StyledLink href="/terms-and-conditions" />,
              ]}
            />
          </LabelStyled>
        </DisclaimerContainer>
      </DialogContentStyled>
    </Fragment>
  );
}

const mapStateToProps = (state) => {
  return {
    registering: state.user.registering,
    socialLoggingIn: state.user.socialLoggingIn,
    signUpText: state.navbar.signUpText,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    register: bindActionCreators(register, dispatch),
    hideSnackBar: bindActionCreators(hideSnackBar, dispatch),
    showSnackBar: bindActionCreators(showSnackBar, dispatch),
    socialMediaLogin: bindActionCreators(socialMediaLogin, dispatch),
    updateJobAlertVisitorEmailDialog: bindActionCreators(
      updateJobAlertVisitorEmailDialog,
      dispatch
    ),
    updateResumeConfirmationDialog: bindActionCreators(
      updateResumeConfirmationDialog,
      dispatch
    ),
    updateSignUpProfileDialog: bindActionCreators(
      updateSignUpProfileDialog,
      dispatch
    ),
    updatePDPAStatus: bindActionCreators(updatePDPAStatus, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SignUp);
